import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation, useRouteMatch } from 'react-router';

import type { MenuOption } from '@swf/components/Inputs/components/Menu/Menu';
import Menu from '@swf/components/Inputs/components/Menu/Menu';
import StarsButton from '@swf/components/StarsButton/StarsButton';
import Tooltip from '@swf/components/Tooltip/Tooltip';
import Translation from '@swf/components/Translation/Translation';
import useOnClickOutside from '@swf/core/hooks/useOnClickOutside';
import { useWidget } from '@swf/core/hooks/useWidget';
import type { Dispatch } from '@swf/core/store';
import { ClubRoute, PokerModalRoutes, UserClubRole } from '@swf/core/types/Poker';
import { useCheckUserClubRole, useGetContentRoutes } from '@swf/poker';

import stylesShare from '../../../ShareClub/ShareClub.scss';
import useCopyInstructions from '../../../ShareClub/hooks/useCopyInstructions';
import useGetClubPassword from '../../hooks/useGetClubPassword';
import styles from '../ActionsDropdown/ActionsDropdown.scss';
import DotsIcon from '../assets/dots.svg';

import {
  TXTCLI_Club_Settings,
  TXTCLI_Create_Tournament,
  TXTCLI_Share,
  TXTWCL_Copied,
  TXTWCL_Create_Cash_Game
} from './ActionsDropdownMobile.messages.json';

export enum Action {
  'Share' = 'Share'
}

const adminOptions = [
  { text: <Translation {...TXTCLI_Share} />, value: Action.Share },
  { text: <Translation {...TXTWCL_Create_Cash_Game} />, value: PokerModalRoutes.createCashGame },
  {
    text: <Translation {...TXTCLI_Create_Tournament} />,
    value: PokerModalRoutes.createTournament
  }
];

const ActionsDropdownMobile: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { widgetId } = useWidget();
  const triggerRef = useRef<HTMLDivElement>(null);
  const history = useHistory();
  const { search } = useLocation();
  const { clubsPath } = useGetContentRoutes();
  const {
    params: { clubId }
  } = useRouteMatch<{ clubId: string }>();
  const { modal } = useDispatch<Dispatch>();
  const { copied, handleCopy, resetCopy } = useCopyInstructions();
  const { clubPassword } = useGetClubPassword(Number(clubId));
  const isAdminOrManager = useCheckUserClubRole(clubId, [UserClubRole.Admin, UserClubRole.Manager]);

  const settingsOption = [
    {
      text: <Translation {...TXTCLI_Club_Settings} />,
      value: `${clubsPath}/${clubId}/${ClubRoute.Settings}`
    }
  ];
  const options = isAdminOrManager ? [...adminOptions, ...settingsOption] : settingsOption;

  useOnClickOutside(triggerRef, () => {
    setIsOpen(false);
    resetCopy();
  });

  useEffect(() => {
    // reset copied state if user switches to another window
    window.addEventListener('blur', resetCopy);
    return () => window.removeEventListener('blur', resetCopy);
  }, [resetCopy]);

  const handleSelect = ({ value }: MenuOption) => {
    if (value === PokerModalRoutes.createCashGame || value === PokerModalRoutes.createTournament) {
      setIsOpen(false);
      modal.enqueue({
        path: `${value}`,
        locationState: { clubId: Number(clubId), widgetId }
      });
    } else if (value === Action.Share && clubPassword) {
      handleCopy({ clubId: Number(clubId), invitationCode: clubPassword });
    } else {
      const pathname = `${value}`;
      history.push({ pathname, search });
    }
  };

  return (
    <div ref={triggerRef} className={styles.container}>
      <StarsButton
        disabled={!options.length}
        variant="text"
        className={styles.button}
        onClick={() => setIsOpen(!isOpen)}
        icon={<DotsIcon className={styles.icon} />}
      />
      {isOpen && options.length > 0 && (
        <>
          <Menu className={styles.menu} onSelect={handleSelect} options={options} />
          <Tooltip isOpen={copied && isOpen} createPortal={false} placement="top">
            {({ ref, placement }) => (
              <div className={stylesShare.tooltip} ref={ref} data-popper-placement={placement}>
                <Translation {...TXTWCL_Copied} />
              </div>
            )}
          </Tooltip>
        </>
      )}
    </div>
  );
};

export default ActionsDropdownMobile;
